import React, { useState, useRef } from 'react';

// ─── Sub-components defined OUTSIDE AdminPlantEditor ───────────────────────
// This is critical: if these were inside, React would remount them on every
// keystroke (new function ref = new component type = unmount/mount = focus lost).

// Choice constants from backend models
const GROWTH_FORM_CHOICES = [
    { value: '', label: 'Nicht angegeben' },
    { value: 'TREE', label: 'Baum' },
    { value: 'SHRUB', label: 'Strauch' },
    { value: 'BUSH', label: 'Busch' },
    { value: 'PERENNIAL', label: 'Staude' },
    { value: 'ANNUAL', label: 'Einjährig' },
    { value: 'BIENNIAL', label: 'Zweijährig' },
    { value: 'CLIMBER', label: 'Kletterpflanze' },
    { value: 'GROUND_COVER', label: 'Bodendecker' },
    { value: 'GRASS', label: 'Gras' },
    { value: 'FERN', label: 'Farn' },
    { value: 'OTHER', label: 'Sonstiges' },
];

const FLOWERING_SEASON_CHOICES = [
    { value: '', label: 'Nicht angegeben' },
    { value: 'SPRING', label: 'Frühling' },
    { value: 'EARLY_SUMMER', label: 'Frühsommer' },
    { value: 'SUMMER', label: 'Sommer' },
    { value: 'LATE_SUMMER', label: 'Spätsommer' },
    { value: 'AUTUMN', label: 'Herbst' },
    { value: 'WINTER', label: 'Winter' },
    { value: 'ALL_YEAR', label: 'Ganzjährig' },
];

const FLOWER_COLOR_CHOICES = [
    { value: '', label: 'Nicht angegeben' },
    { value: 'WHITE', label: 'Weiß' },
    { value: 'YELLOW', label: 'Gelb' },
    { value: 'ORANGE', label: 'Orange' },
    { value: 'RED', label: 'Rot' },
    { value: 'PINK', label: 'Rosa' },
    { value: 'PURPLE', label: 'Lila/Violett' },
    { value: 'BLUE', label: 'Blau' },
    { value: 'GREEN', label: 'Grün' },
    { value: 'BROWN', label: 'Braun' },
    { value: 'MIXED', label: 'Gemischt' },
];

const LOCATION_CHOICES = [
    { value: '', label: 'Nicht angegeben' },
    { value: 'FULL_SUN', label: 'Volle Sonne' },
    { value: 'PARTIAL_SUN', label: 'Halbschatten' },
    { value: 'SHADE', label: 'Schatten' },
    { value: 'FULL_SUN_TO_PARTIAL', label: 'Sonne bis Halbschatten' },
    { value: 'PARTIAL_TO_SHADE', label: 'Halbschatten bis Schatten' },
];

const SOIL_TYPE_CHOICES = [
    { value: '', label: 'Nicht angegeben' },
    { value: 'NORMAL', label: 'Normal' },
    { value: 'SANDY', label: 'Sandig' },
    { value: 'CLAY', label: 'Lehmig' },
    { value: 'LOAMY', label: 'Lehmig-humos' },
    { value: 'HUMUS', label: 'Humos' },
    { value: 'ROCKY', label: 'Steinig' },
    { value: 'WET', label: 'Feucht' },
    { value: 'DRY', label: 'Trocken' },
    { value: 'ACIDIC', label: 'Sauer' },
    { value: 'ALKALINE', label: 'Alkalisch' },
];

const WINTER_HARDINESS_CHOICES = [
    { value: '', label: 'Nicht angegeben' },
    { value: 'H1', label: 'H1 (> 15°C)' },
    { value: 'H2', label: 'H2 (10-15°C)' },
    { value: 'H3', label: 'H3 (5-10°C)' },
    { value: 'H4', label: 'H4 (bis -10°C)' },
    { value: 'H5', label: 'H5 (bis -15°C)' },
    { value: 'H6', label: 'H6 (bis -20°C)' },
    { value: 'H7', label: 'H7 (< -20°C)' },
];

const LIME_TOLERANCE_CHOICES = [
    { value: '', label: 'Nicht angegeben' },
    { value: 'LIEBEND', label: 'Liebend' },
    { value: 'TOLERIEREND', label: 'Tolerierend' },
    { value: 'EMPFINDLICH', label: 'Empfindlich' },
];

const SOCIALITY_CHOICES = [
    { value: '', label: 'Nicht angegeben' },
    { value: 'EINZELN', label: 'Einzeln' },
    { value: 'TRUPPS', label: 'Trupps' },
    { value: 'GRUPPEN', label: 'Gruppen' },
    { value: 'FLÄCHIG', label: 'Flächig' },
    { value: 'GROSSFLÄCHIG', label: 'Großflächig' },
];

const Field = ({ label, name, value, onChange, multiline = false, type = 'text', half = false, showMagic = false, onMagic, isSuggesting = false, maxLength }) => (
    <div className={half ? 'col-span-1' : ''}>
        <div className="flex justify-between items-end mb-1.5">
            <label className="block text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                {label}
                {maxLength && <span className="ml-2 lowercase font-normal">({value?.length || 0}/{maxLength})</span>}
            </label>
            {showMagic && (
                <button
                    type="button"
                    onClick={onMagic}
                    disabled={isSuggesting}
                    className="text-[10px] flex items-center gap-1 text-plantlix-500 hover:text-plantlix-600 dark:text-plantlix-400 font-bold transition-colors uppercase tracking-tight"
                    title="KI-Vorschlag generieren (max. 160 Zeichen)"
                >
                    {isSuggesting ? <span className="animate-spin">⏳</span> : <span>🪄 Vorschlag</span>}
                </button>
            )}
        </div>
        {multiline ? (
            <textarea
                name={name}
                value={value}
                onChange={onChange}
                rows={3}
                placeholder={maxLength ? `Max. ${maxLength} Zeichen...` : "Text eingeben..."}
                className={`w-full rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-plantlix-400 focus:border-transparent resize-y transition placeholder-gray-400 ${maxLength && value?.length > maxLength ? 'border-red-400 dark:border-red-500 ring-1 ring-red-400' : ''}`}
            />
        ) : (
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={maxLength ? `Max. ${maxLength} Zeichen...` : ""}
                className={`w-full rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-plantlix-400 focus:border-transparent transition placeholder-gray-400 ${maxLength && value?.length > maxLength ? 'border-red-400 dark:border-red-500 ring-1 ring-red-400' : ''}`}
            />
        )}
    </div>
);

const Select = ({ label, name, value, onChange, options, half = false }) => (
    <div className={half ? 'col-span-1' : ''}>
        <label className="block text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1.5">
            {label}
        </label>
        <div className="relative">
            <select
                name={name}
                value={value}
                onChange={onChange}
                className="w-full rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-plantlix-400 focus:border-transparent transition appearance-none"
            >
                {options.map(opt => (
                    <option key={opt.value} value={opt.value} className="dark:bg-[#1a1a1a]">
                        {opt.label}
                    </option>
                ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-3.5 pointer-events-none text-gray-400">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>
        </div>
    </div>
);

const Toggle = ({ label, name, checked, onChange }) => (
    <label className="flex items-center gap-3 cursor-pointer select-none group">
        <div className="relative flex-shrink-0">
            <input type="checkbox" name={name} checked={checked} onChange={onChange} className="sr-only" />
            <div className={`w-11 h-6 rounded-full transition-colors duration-200 ${checked ? 'bg-plantlix-500' : 'bg-gray-200 dark:bg-gray-700'}`} />
            <div className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${checked ? 'translate-x-5' : ''}`} />
        </div>
        <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">{label}</span>
    </label>
);

const SectionHeader = ({ emoji, title, color = 'emerald' }) => {
    const colors = {
        emerald: 'text-plantlix-600 dark:text-plantlix-400 border-plantlix-100 dark:border-plantlix-900/40 bg-plantlix-50 dark:bg-plantlix-900/10',
        blue:    'text-blue-600 dark:text-blue-400 border-blue-100 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/10',
        purple:  'text-purple-600 dark:text-purple-400 border-purple-100 dark:border-purple-900/40 bg-purple-50 dark:bg-purple-900/10',
        teal:    'text-plantlix-600 dark:text-plantlix-400 border-plantlix-100 dark:border-plantlix-900/40 bg-plantlix-50 dark:bg-plantlix-900/10',
        amber:   'text-amber-600 dark:text-amber-400 border-amber-100 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/10',
        gray:    'text-gray-600 dark:text-gray-400 border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/20',
    };
    return (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-bold uppercase tracking-widest mb-4 ${colors[color]}`}>
            <span>{emoji}</span>
            <span>{title}</span>
        </div>
    );
};

// ─── Main Component ─────────────────────────────────────────────────────────

const AdminPlantEditor = ({ plant, categories = [], fetchWithAuth, onSaved, onClose }) => {
    const [form, setForm] = useState({
        botanical_name:      plant.botanical_name || '',
        german_name:         plant.german_name || '',
        family:              plant.family || '',
        category:            plant.category || '',
        description:         plant.notes || '',
        origin:              plant.origin || '',
        distribution:        plant.distribution || '',
        care_notes:          plant.care_notes || '',
        watering_needs:      plant.watering_needs || '',
        fertilizer_needs:    plant.fertilizer_needs || '',
        pruning_notes:       plant.pruning_notes || '',
        leaves:              plant.leaves || '',
        fruits:              plant.fruits || '',
        bark_description:    plant.bark_description || '',
        special_features:    plant.special_features || '',
        artname_erklärung:   plant['artname_erklärung'] || '',
        life_zone:           plant.life_zone || '',
        life_zones:          Array.isArray(plant.life_zones) ? plant.life_zones : [],
        sociality:           plant.sociality || '',
        wuchs:               plant.wuchs || '',
        height_min_cm:       plant.height_min_cm ?? '',
        height_max_cm:       plant.height_max_cm ?? '',
        width_min_cm:        plant.width_min_cm ?? '',
        width_max_cm:        plant.width_max_cm ?? '',
        is_poisonous:        plant.is_poisonous || false,
        is_edible:           plant.is_edible || false,
        is_heimisch:         plant.is_heimisch || false,
        is_protected:        plant.is_protected || false,
        // Added fields
        growth_form:         plant.growth_form || '',
        flowering_season:    plant.flowering_season || '',
        flowering_months:    plant.flowering_months || '',
        flower_color:        plant.flower_color || '',
        flower_inflorescence: plant.flower_inflorescence || '',
        location_preference: plant.location_preference || '',
        soil_type:           plant.soil_type || '',
        soil_ph:             plant.soil_ph || '',
        soil_requirements:   plant.soil_requirements || '',
        nutrient_needs:      plant.nutrient_needs || '',
        winter_hardiness:    plant.winter_hardiness || '',
        lime_tolerance:      plant.lime_tolerance || '',
        is_evergreen:        plant.is_evergreen || false,
        is_deciduous:        plant.is_deciduous || false,
        is_intermediate_exam_subject: plant.is_intermediate_exam_subject || false,
        autumn_color:        plant.autumn_color || '',
        synonyms:            plant.synonyms || '',
        common_names:        plant.common_names || '',
        shortened_data:      plant.shortened_data || {},
    });

    const [suggestingFields, setSuggestingFields] = useState(new Set());
    const [isAutomatingAll, setIsAutomatingAll] = useState(false);

    const [localImages, setLocalImages] = useState(plant.images || []);
    const [uploadFiles, setUploadFiles] = useState([]);
    const [uploading, setUploading]     = useState(false);
    const [saving, setSaving]           = useState(false);
    const [saveError, setSaveError]     = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [deletingIds, setDeletingIds] = useState(new Set());
    const fileInputRef = useRef(null);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Handle shortened_data fields (prefix: short_)
        if (name.startsWith('short_')) {
            const fieldKey = name.replace('short_', '');
            setForm(prev => ({
                ...prev,
                shortened_data: {
                    ...prev.shortened_data,
                    [fieldKey]: value
                }
            }));
            return;
        }

        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSuggestion = async (fieldName, fieldLabel) => {
        setSuggestingFields(prev => new Set([...prev, fieldName]));
        try {
            const res = await fetchWithAuth(`/api/plants/${plant.id}/research_suggestion/?field=${encodeURIComponent(fieldLabel)}`);
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    setForm(prev => {
                        const updatedShortened = {
                            ...prev.shortened_data,
                            [fieldName]: data.suggestion
                        };
                        
                        // Auto-save the specific change
                        fetchWithAuth(`/api/plants/${plant.id}/`, {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ shortened_data: updatedShortened })
                        }).then(r => r.ok && r.json()).then(updated => {
                            if (updated && onSaved) onSaved(updated);
                        }).catch(console.error);
                        
                        return { ...prev, shortened_data: updatedShortened };
                    });
                }
            } else {
                throw new Error("KI-Vorschlag fehlgeschlagen");
            }
        } catch (e) {
            setSaveError(e.message);
        } finally {
            setSuggestingFields(prev => {
                const s = new Set(prev);
                s.delete(fieldName);
                return s;
            });
        }
    };

    const handleAutomateAll = async () => {
        setIsAutomatingAll(true);
        setSaveError(null);
        try {
            const res = await fetchWithAuth(`/api/plants/${plant.id}/research_all_suggestions/`);
            if (res.ok) {
                const data = await res.json();
                if (data.suggestions) {
                    setForm(prev => {
                        const updatedShortened = {
                            ...prev.shortened_data,
                            ...data.suggestions
                        };
                        
                        // Auto-save the specific change
                        fetchWithAuth(`/api/plants/${plant.id}/`, {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ shortened_data: updatedShortened })
                        }).then(r => r.ok && r.json()).then(updated => {
                            if (updated && onSaved) onSaved(updated);
                        }).catch(console.error);
                        
                        return { ...prev, shortened_data: updatedShortened };
                    });
                }
            } else {
                throw new Error("KI-Automatisierung fehlgeschlagen");
            }
        } catch (e) {
            setSaveError(e.message);
        } finally {
            setIsAutomatingAll(false);
        }
    };

    // ── Save ──────────────────────────────────────────────────────────────
    const handleSave = async () => {
        setSaving(true);
        setSaveError(null);
        setSaveSuccess(false);
        try {
            const payload = {
                botanical_name:     form.botanical_name,
                german_name:        form.german_name,
                family:             form.family,
                notes:              form.description,
                origin:             form.origin,
                distribution:       form.distribution,
                care_notes:         form.care_notes,
                watering_needs:     form.watering_needs,
                fertilizer_needs:   form.fertilizer_needs,
                pruning_notes:      form.pruning_notes,
                leaves:             form.leaves,
                fruits:             form.fruits,
                bark_description:   form.bark_description,
                special_features:   form.special_features,
                'artname_erklärung': form['artname_erklärung'],
                life_zone:          form.life_zone,
                life_zones:         form.life_zones,

                sociality:          form.sociality,
                wuchs:              form.wuchs,
                height_min_cm:      form.height_min_cm !== '' ? Number(form.height_min_cm) : null,
                height_max_cm:      form.height_max_cm !== '' ? Number(form.height_max_cm) : null,
                width_min_cm:       form.width_min_cm  !== '' ? Number(form.width_min_cm)  : null,
                width_max_cm:       form.width_max_cm  !== '' ? Number(form.width_max_cm)  : null,
                is_poisonous:       form.is_poisonous,
                is_edible:          form.is_edible,
                is_heimisch:        form.is_heimisch,
                is_protected:       form.is_protected,
                category:           form.category || null,
                growth_form:        form.growth_form || null,
                flowering_season:   form.flowering_season || null,
                flowering_months:   form.flowering_months,
                flower_color:       form.flower_color || null,
                flower_inflorescence: form.flower_inflorescence,
                location_preference: form.location_preference || null,
                soil_type:          form.soil_type || null,
                soil_ph:            form.soil_ph,
                soil_requirements:  form.soil_requirements,
                nutrient_needs:     form.nutrient_needs,
                winter_hardiness:   form.winter_hardiness || null,
                lime_tolerance:     form.lime_tolerance || null,
                is_evergreen:       form.is_evergreen,
                is_deciduous:       form.is_deciduous,
                is_intermediate_exam_subject: form.is_intermediate_exam_subject,
                autumn_color:       form.autumn_color,
                synonyms:           form.synonyms,
                common_names:       form.common_names,
                shortened_data:     form.shortened_data,
            };
            const res = await fetchWithAuth(`/api/plants/${plant.id}/`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(JSON.stringify(err));
            }
            const updated = await res.json();
            setSaveSuccess(true);
            onSaved?.(updated);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (e) {
            setSaveError(e.message || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    // ── Image upload ──────────────────────────────────────────────────────
    const handleImageSelect = (e) => {
        const files = Array.from(e.target.files);
        setUploadFiles(prev => [
            ...prev,
            ...files.map(f => ({ file: f, preview: URL.createObjectURL(f) }))
        ]);
        e.target.value = '';
    };

    const removeUploadFile = (idx) => {
        setUploadFiles(prev => {
            URL.revokeObjectURL(prev[idx].preview);
            return prev.filter((_, i) => i !== idx);
        });
    };

    const handleUpload = async () => {
        if (!uploadFiles.length) return;
        setUploading(true);
        const fd = new FormData();
        uploadFiles.forEach(({ file }) => fd.append('images', file));
        try {
            const res = await fetchWithAuth(`/api/plants/${plant.id}/upload/`, {
                method: 'POST',
                body: fd,
            });
            if (!res.ok) throw new Error('Upload fehlgeschlagen');
            const data = await res.json();
            setLocalImages(prev => [...prev, ...(data.uploaded || [])]);
            setUploadFiles([]);
            onSaved?.(null);
        } catch (e) {
            setSaveError(e.message);
        } finally {
            setUploading(false);
        }
    };

    // ── Image delete ──────────────────────────────────────────────────────
    const handleDeleteImage = async (imageId) => {
        setDeletingIds(prev => new Set([...prev, imageId]));
        try {
            const res = await fetchWithAuth(`/api/plants/images/${imageId}/delete/`, { method: 'DELETE' });
            if (!res.ok) throw new Error('Fehler beim Löschen');
            setLocalImages(prev => prev.filter(img => img.id !== imageId));
            onSaved?.(null);
        } catch (e) {
            setSaveError(e.message);
        } finally {
            setDeletingIds(prev => { const s = new Set(prev); s.delete(imageId); return s; });
        }
    };

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <div className="space-y-7 animate-fadeIn">

            {/* Status banner */}
            {(saveSuccess || saveError) && (
                <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium border
                    ${saveSuccess
                        ? 'bg-plantlix-50 dark:bg-plantlix-900/20 text-plantlix-700 dark:text-plantlix-300 border-plantlix-200 dark:border-plantlix-800'
                        : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800'}`}>
                    <span>{saveSuccess ? '✓' : '⚠'}</span>
                    <span>{saveSuccess ? 'Erfolgreich gespeichert' : saveError}</span>
                    {saveError && (
                        <button onClick={() => setSaveError(null)} className="ml-auto text-red-400 hover:text-red-600">✕</button>
                    )}
                </div>
            )}

            {/* One-Click AI Button */}
            <div className="flex justify-end">
                <button
                    onClick={handleAutomateAll}
                    disabled={isAutomatingAll}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-plantlix-500/10 hover:bg-plantlix-500/20 text-plantlix-600 dark:text-plantlix-400 border border-plantlix-500/30 font-bold text-sm transition-all"
                >
                    {isAutomatingAll ? (
                        <>
                            <div className="w-4 h-4 border-2 border-plantlix-500 border-t-transparent rounded-full animate-spin" />
                            <span>Generiere alle Kurzfassungen...</span>
                        </>
                    ) : (
                        <>
                            <span>🪄</span>
                            <span>Alle Kurzfassungen KI-Automatisieren</span>
                        </>
                    )}
                </button>
            </div>

            {/* ── Grunddaten ── */}
            <section>
                <SectionHeader emoji="🌿" title="Grunddaten" color="emerald" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <Field label="Botanischer Name"    name="botanical_name"    value={form.botanical_name}    onChange={handleChange} />
                    <Field label="Deutscher Name"      name="german_name"       value={form.german_name}       onChange={handleChange} />
                    <Select 
                        label="Kategorie" 
                        name="category" 
                        value={form.category} 
                        onChange={handleChange}
                        options={[{ value: '', label: 'Keine Kategorie' }, ...categories.map(c => ({ value: c.id, label: c.name }))]}
                    />
                    <Field label="Familie"             name="family"            value={form.family}            onChange={handleChange} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Toggle label="Zwischenprüfungs-Pflanze" name="is_intermediate_exam_subject" checked={form.is_intermediate_exam_subject} onChange={handleChange} />
                    <Field label="Artname-Erklärung (Original)" name="artname_erklärung" value={form.artname_erklärung} onChange={handleChange} multiline />
                    <Field 
                        label="Artname-Erklärung (Kurzfassung)" 
                        name="short_artname_erklärung" 
                        value={form.shortened_data.artname_erklärung || ''} 
                        onChange={handleChange} 
                        multiline 
                        maxLength={160}
                        showMagic
                        onMagic={() => handleSuggestion('artname_erklärung', 'Erklärung des Artnamens')}
                        isSuggesting={suggestingFields.has('artname_erklärung')}
                    />
                </div>
            </section>

            {/* ── Wuchs & Standort ── */}
            <section>
                <SectionHeader emoji="🌳" title="Wuchs & Standort" color="teal" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <Select label="Wuchsform"          name="growth_form"       value={form.growth_form}       onChange={handleChange} options={GROWTH_FORM_CHOICES} />
                    <Select label="Geselligkeit"       name="sociality"         value={form.sociality}         onChange={handleChange} options={SOCIALITY_CHOICES} />
                    <Select label="Standort-Präferenz" name="location_preference" value={form.location_preference} onChange={handleChange} options={LOCATION_CHOICES} />
                </div>
                {/* Lebensbereich Multi-Checkbox */}
                <div className="mb-4">
                    <label className="block text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2">
                        Lebensbereich (Hansen-Stahl-System)
                        {form.life_zones.length > 0 && (
                            <span className="ml-2 text-plantlix-500 normal-case font-normal tracking-normal">✓ Manuell gesetzt – KI-Anreicherung gesperrt</span>
                        )}
                    </label>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 p-3 bg-gray-50 dark:bg-white/5 rounded-xl border border-gray-200 dark:border-white/10">
                        {[
                            { code: 'G',     label: 'G – Gehölz' },
                            { code: 'GR',    label: 'GR – Gehölzrand' },
                            { code: 'Fr',    label: 'Fr – Freifläche' },
                            { code: 'B',     label: 'B – Beet' },
                            { code: 'SH',    label: 'SH – Steppe/Heide' },
                            { code: 'H',     label: 'H – Heide' },
                            { code: 'St',    label: 'St – Steinanlage' },
                            { code: 'FS',    label: 'FS – Fels-Steppe' },
                            { code: 'M',     label: 'M – Matten' },
                            { code: 'SF',    label: 'SF – Stein-Fugen' },
                            { code: 'MK',    label: 'MK – Mauer-Kronen' },
                            { code: 'A',     label: 'A – Alpinum' },
                            { code: 'WR',    label: 'WR – Wasser-Rand' },
                            { code: 'W',     label: 'W – Wasserpfl.' },
                            { code: 'KÜBEL', label: 'KÜBEL – Kübel' },
                        ].map(({ code, label }) => (
                            <label key={code} className="flex items-center gap-1.5 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    checked={form.life_zones.includes(code)}
                                    onChange={() => setForm(prev => {
                                        const current = prev.life_zones || [];
                                        const next = current.includes(code)
                                            ? current.filter(c => c !== code)
                                            : [...current, code];
                                        return { ...prev, life_zones: next, life_zone: next.join(', ') };
                                    })}
                                    className="w-3.5 h-3.5 rounded border-gray-300 text-plantlix-500 focus:ring-plantlix-400"
                                />
                                <span className="text-xs text-gray-700 dark:text-gray-300 group-hover:text-plantlix-500 transition-colors">{label}</span>
                            </label>
                        ))}
                    </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <Toggle label="Heimisch"   name="is_heimisch"  checked={form.is_heimisch}  onChange={handleChange} />
                    <Toggle label="Geschützt"  name="is_protected" checked={form.is_protected} onChange={handleChange} />
                </div>
            </section>


            {/* ── Blüte & Früchte ── */}
            <section>
                <SectionHeader emoji="🌸" title="Blüte & Früchte" color="purple" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <Select label="Blütezeit (Saison)" name="flowering_season" value={form.flowering_season} onChange={handleChange} options={FLOWERING_SEASON_CHOICES} />
                    <Field  label="Blütezeit (Monate)"  name="flowering_months" value={form.flowering_months} onChange={handleChange} />
                    <Select label="Blütenfarbe"        name="flower_color"     value={form.flower_color}     onChange={handleChange} options={FLOWER_COLOR_CHOICES} />
                    <Field  label="Blütenstand"        name="flower_inflorescence" value={form.flower_inflorescence} onChange={handleChange} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-4">
                    <div className="space-y-4">
                        <Field label="Blätter (Original)" name="leaves" value={form.leaves} onChange={handleChange} multiline />
                        <Field 
                            label="Blätter (Kurzfassung)" 
                            name="short_leaves" 
                            value={form.shortened_data.leaves || ''} 
                            onChange={handleChange} 
                            multiline 
                            maxLength={160}
                            showMagic
                            onMagic={() => handleSuggestion('leaves', 'Blätter (Stellung, Form, Rand)')}
                            isSuggesting={suggestingFields.has('leaves')}
                        />
                    </div>
                    <div className="space-y-4">
                        <Field label="Früchte (Original)" name="fruits" value={form.fruits} onChange={handleChange} multiline />
                        <Field 
                            label="Früchte (Kurzfassung)" 
                            name="short_fruits" 
                            value={form.shortened_data.fruits || ''} 
                            onChange={handleChange} 
                            multiline 
                            maxLength={160}
                            showMagic
                            onMagic={() => handleSuggestion('fruits', 'Früchte (Art, Stand)')}
                            isSuggesting={suggestingFields.has('fruits')}
                        />
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <Field label="Herbstfärbung"       name="autumn_color"     value={form.autumn_color}     onChange={handleChange} />
                    <Field 
                        label="Herbstfärbung (Kurzfassung)" 
                        name="short_autumn_color" 
                        value={form.shortened_data.autumn_color || ''} 
                        onChange={handleChange} 
                        maxLength={160}
                        showMagic
                        onMagic={() => handleSuggestion('autumn_color', 'Herbstfärbung')}
                        isSuggesting={suggestingFields.has('autumn_color')}
                    />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <Toggle label="Immergrün"          name="is_evergreen"     checked={form.is_evergreen}   onChange={handleChange} />
                    <Toggle label="Laubabwerfend"      name="is_deciduous"     checked={form.is_deciduous}   onChange={handleChange} />
                </div>
            </section>

            {/* ── Beschreibung & Texte ── */}
            <section>
                <SectionHeader emoji="📝" title="Beschreibung & Texte" color="blue" />
                <div className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Allgemeine Beschreibung (Original)" name="description" value={form.description} onChange={handleChange} multiline />
                        <Field 
                            label="Beschreibung (Kurzfassung)" 
                            name="short_notes" 
                            value={form.shortened_data.notes || ''} 
                            onChange={handleChange} 
                            multiline 
                            maxLength={160}
                            showMagic
                            onMagic={() => handleSuggestion('notes', 'Allgemeine Beschreibung/Notizen')}
                            isSuggesting={suggestingFields.has('notes')}
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <Field label="Herkunft (Original)" name="origin" value={form.origin} onChange={handleChange} multiline />
                            <Field 
                                label="Herkunft (Kurzfassung)" 
                                name="short_origin" 
                                value={form.shortened_data.origin || ''} 
                                onChange={handleChange} 
                                multiline 
                                maxLength={160}
                                showMagic
                                onMagic={() => handleSuggestion('origin', 'Herkunft')}
                                isSuggesting={suggestingFields.has('origin')}
                            />
                        </div>
                        <div className="space-y-4">
                            <Field label="Verbreitung (Original)" name="distribution" value={form.distribution} onChange={handleChange} multiline />
                            <Field 
                                label="Verbreitung (Kurzfassung)" 
                                name="short_distribution" 
                                value={form.shortened_data.distribution || ''} 
                                onChange={handleChange} 
                                multiline 
                                maxLength={160}
                                showMagic
                                onMagic={() => handleSuggestion('distribution', 'Verbreitung')}
                                isSuggesting={suggestingFields.has('distribution')}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Rinde/Borke (Original)" name="bark_description" value={form.bark_description} onChange={handleChange} multiline />
                        <Field 
                            label="Rinde/Borke (Kurzfassung)" 
                            name="short_bark_description" 
                            value={form.shortened_data.bark_description || ''} 
                            onChange={handleChange} 
                            multiline 
                            maxLength={160}
                            showMagic
                            onMagic={() => handleSuggestion('bark_description', 'Rinde/Borke')}
                            isSuggesting={suggestingFields.has('bark_description')}
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Besonderheiten (Original)" name="special_features" value={form.special_features} onChange={handleChange} multiline />
                        <Field 
                            label="Besonderheiten (Kurzfassung)" 
                            name="short_special_features" 
                            value={form.shortened_data.special_features || ''} 
                            onChange={handleChange} 
                            multiline 
                            maxLength={160}
                            showMagic
                            onMagic={() => handleSuggestion('special_features', 'Besondere Merkmale')}
                            isSuggesting={suggestingFields.has('special_features')}
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Wuchs (Original)" name="wuchs" value={form.wuchs} onChange={handleChange} multiline />
                        <Field 
                            label="Wuchs (Kurzfassung)" 
                            name="short_wuchs" 
                            value={form.shortened_data.wuchs || ''} 
                            onChange={handleChange} 
                            multiline 
                            maxLength={160}
                            showMagic
                            onMagic={() => handleSuggestion('wuchs', 'Wuchsform/Wuchs')}
                            isSuggesting={suggestingFields.has('wuchs')}
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Synonyme"       name="synonyms"     value={form.synonyms}     onChange={handleChange} multiline />
                        <Field label="Weitere Namen"  name="common_names" value={form.common_names} onChange={handleChange} multiline />
                    </div>
                </div>
            </section>

            {/* ── Maße ── */}
            <section>
                <SectionHeader emoji="📐" title="Maße" color="purple" />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <Field label="Höhe min (cm)"   name="height_min_cm" value={form.height_min_cm} onChange={handleChange} type="number" />
                    <Field label="Höhe max (cm)"   name="height_max_cm" value={form.height_max_cm} onChange={handleChange} type="number" />
                    <Field label="Breite min (cm)" name="width_min_cm"  value={form.width_min_cm}  onChange={handleChange} type="number" />
                    <Field label="Breite max (cm)" name="width_max_cm"  value={form.width_max_cm}  onChange={handleChange} type="number" />
                </div>
            </section>

            {/* ── Boden & Wasser ── */}
            <section>
                <SectionHeader emoji="💧" title="Boden & Wasser" color="amber" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <Select label="Bodentyp"           name="soil_type"        value={form.soil_type}        onChange={handleChange} options={SOIL_TYPE_CHOICES} />
                    <Field  label="pH-Wert"            name="soil_ph"          value={form.soil_ph}          onChange={handleChange} />
                    <Select label="Kalktoleranz"       name="lime_tolerance"   value={form.lime_tolerance}   onChange={handleChange} options={LIME_TOLERANCE_CHOICES} />
                    <Select label="Winterhärte"        name="winter_hardiness" value={form.winter_hardiness} onChange={handleChange} options={WINTER_HARDINESS_CHOICES} />
                </div>
                <div className="space-y-4">
                    <Field label="Bodenansprüche (Details)" name="soil_requirements" value={form.soil_requirements} onChange={handleChange} multiline />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Wasserbedarf"    name="watering_needs"   value={form.watering_needs}   onChange={handleChange} />
                        <Field label="Düngerbedarf"    name="fertilizer_needs" value={form.fertilizer_needs} onChange={handleChange} />
                    </div>
                    <Field label="Nährstoffbedarf"     name="nutrient_needs"   value={form.nutrient_needs}   onChange={handleChange} />
                </div>
            </section>

            {/* ── Pflege ── */}
            <section>
                <SectionHeader emoji="✂️" title="Pflege" color="teal" />
                <div className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Pflege-Hinweise (Original)" name="care_notes" value={form.care_notes} onChange={handleChange} multiline />
                        <Field 
                            label="Pflege-Hinweise (Kurzfassung)" 
                            name="short_care_notes" 
                            value={form.shortened_data.care_notes || ''} 
                            onChange={handleChange} 
                            multiline 
                            maxLength={160}
                            showMagic
                            onMagic={() => handleSuggestion('care_notes', 'Pflegehinweise')}
                            isSuggesting={suggestingFields.has('care_notes')}
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Schnitt-Hinweise (Original)" name="pruning_notes" value={form.pruning_notes} onChange={handleChange} multiline />
                        <Field 
                            label="Schnitt-Hinweise (Kurzfassung)" 
                            name="short_pruning_notes" 
                            value={form.shortened_data.pruning_notes || ''} 
                            onChange={handleChange} 
                            multiline 
                            maxLength={160}
                            showMagic
                            onMagic={() => handleSuggestion('pruning_notes', 'Schnitt-Hinweise')}
                            isSuggesting={suggestingFields.has('pruning_notes')}
                        />
                    </div>
                </div>
            </section>

            {/* ── Eigenschaften ── */}
            <section>
                <SectionHeader emoji="⚠️" title="Eigenschaften" color="amber" />
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                    <Toggle label="Giftig"     name="is_poisonous" checked={form.is_poisonous} onChange={handleChange} />
                    <Toggle label="Essbar"     name="is_edible"    checked={form.is_edible}    onChange={handleChange} />
                </div>
            </section>

            {/* ── Bilder ── */}
            <section>
                <SectionHeader emoji="🖼️" title="Bilder" color="gray" />

                {localImages.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                        {localImages.map(img => (
                            <div key={img.id} className="relative group rounded-xl overflow-hidden border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5 aspect-[4/3]">
                                <img
                                    src={img.image_thumbnail || img.image}
                                    alt={img.caption || ''}
                                    className="w-full h-full object-cover"
                                />
                                <button
                                    onClick={() => handleDeleteImage(img.id)}
                                    disabled={deletingIds.has(img.id)}
                                    className="absolute inset-0 flex items-center justify-center bg-red-600/0 group-hover:bg-red-600/80 transition-all text-white font-semibold text-sm opacity-0 group-hover:opacity-100"
                                >
                                    {deletingIds.has(img.id)
                                        ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        : <span className="flex items-center gap-1.5 drop-shadow">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                            Löschen
                                          </span>
                                    }
                                </button>
                                {img.caption && (
                                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs px-2 py-1 truncate">{img.caption}</div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Drop zone */}
                <div
                    className="border-2 border-dashed border-gray-200 dark:border-white/10 rounded-2xl p-6 hover:border-plantlix-300 dark:hover:border-plantlix-700 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleImageSelect} className="hidden" />
                    <div className="flex flex-col items-center gap-2 text-gray-400 hover:text-plantlix-500 transition-colors">
                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm font-medium">Klicken zum Hinzufügen von Bildern</span>
                        <span className="text-xs text-gray-400">JPG, PNG, WebP – max. 10 MB</span>
                    </div>

                    {uploadFiles.length > 0 && (
                        <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2" onClick={e => e.stopPropagation()}>
                            {uploadFiles.map((f, i) => (
                                <div key={i} className="relative aspect-[4/3] rounded-lg overflow-hidden bg-gray-100 dark:bg-white/5">
                                    <img src={f.preview} alt="" className="w-full h-full object-cover" />
                                    <button
                                        onClick={() => removeUploadFile(i)}
                                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-[10px] font-bold hover:bg-red-600 transition"
                                    >✕</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {uploadFiles.length > 0 && (
                    <button
                        onClick={handleUpload}
                        disabled={uploading}
                        className="mt-3 w-full py-2.5 rounded-xl bg-plantlix-500 hover:bg-plantlix-600 disabled:opacity-60 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2"
                    >
                        {uploading
                            ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Wird hochgeladen…</>
                            : <>{uploadFiles.length} Bild{uploadFiles.length > 1 ? 'er' : ''} hochladen</>
                        }
                    </button>
                )}
            </section>

            {/* ── Action bar ── */}
            <div className="sticky bottom-0 -mx-6 sm:-mx-8 px-6 sm:px-8 py-4 bg-white/80 dark:bg-[#1e1e1e]/80 backdrop-blur-md border-t border-gray-100 dark:border-white/5 flex gap-3">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 py-3 rounded-2xl bg-plantlix-500 hover:bg-plantlix-600 active:bg-plantlix-700 disabled:opacity-60 text-white font-bold text-sm shadow-lg shadow-plantlix-500/20 transition-all flex items-center justify-center gap-2"
                >
                    {saving
                        ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Speichert…</>
                        : '💾 Änderungen speichern'
                    }
                </button>
                <button
                    onClick={onClose}
                    className="px-5 py-3 rounded-2xl border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 font-semibold text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition"
                >
                    Abbrechen
                </button>
            </div>

            {/* Bottom padding so content doesn't hide under sticky bar */}
            <div className="h-20" />
        </div>
    );
};

export default AdminPlantEditor;
